import React, { useState, useRef, useEffect } from 'react';

// Ajuste a URL conforme o ambiente
const IS_PROD = true; // ⚠️ Em produção, mantenha true. Localmente, false.
const API_BASE_URL = IS_PROD ? 'https://sigoapi.jhe-etes.com.br/api' : 'http://localhost:3001/api';
const SERVER_URL = IS_PROD ? 'https://sigoapi.jhe-etes.com.br' : 'http://localhost:3001';

function AdicionarMarcoModal({ isOpen, onClose, empreendimentoId, contratoId, onSuccess, marcoParaEditar }) {
  const fileInputRef = useRef(null);

  // Estados do Formulário
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    data_marco: '',
    status: 'A iniciar',
    imagem_url: ''
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // 🆕 ESTADO PARA O CARD DE CONFIRMAÇÃO DE EXCLUSÃO
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // --- EFEITO: CARREGAR DADOS AO ABRIR ---
  useEffect(() => {
    if (isOpen) {
      setShowDeleteConfirm(false); // Reseta o aviso de exclusão
      
      if (marcoParaEditar) {
        // MODO EDIÇÃO
        setFormData({
          titulo: marcoParaEditar.titulo || '',
          descricao: marcoParaEditar.descricao || '',
          data_marco: marcoParaEditar.data_marco || '',
          status: marcoParaEditar.status || 'A iniciar',
          imagem_url: marcoParaEditar.imagem_url || ''
        });

        // Configura Preview da Imagem Existente
        if (marcoParaEditar.imagem_url) {
          const timestamp = new Date().getTime();
          let urlFinal = marcoParaEditar.imagem_url;
          
          // Ajusta URL relativa se necessário
          if (!urlFinal.startsWith('http')) {
            const path = urlFinal.startsWith('/') ? urlFinal : `/${urlFinal}`;
            urlFinal = `${SERVER_URL}${path}`;
          }
          setPreview(`${urlFinal}?t=${timestamp}`);
        } else {
          setPreview(null);
        }
      } else {
        // MODO CRIAÇÃO
        limparFormulario();
      }
    }
  }, [isOpen, marcoParaEditar]);

  const limparFormulario = () => {
    setFormData({ titulo: '', descricao: '', data_marco: '', status: 'A iniciar', imagem_url: '' });
    setSelectedFile(null);
    setPreview(null);
  };

  // --- HANDLERS DE ARQUIVO ---
  const handleTriggerUpload = () => fileInputRef.current && fileInputRef.current.click();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // 🗑️ 1. SOLICITAR EXCLUSÃO (Abre o card de aviso)
  const solicitarExclusao = () => {
    setShowDeleteConfirm(true);
  };

  // 🗑️ 2. CONFIRMAR EXCLUSÃO (Chama API com Token)
  const confirmarExclusao = async () => {
    if (!marcoParaEditar?.id) return;
    setLoading(true);
    
    // 🔐 PEGA O TOKEN
    const token = localStorage.getItem('token');

    try {
        const response = await fetch(`${API_BASE_URL}/timeline/${marcoParaEditar.id}`, { 
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}` // <--- Token aqui!
            }
        });
        
        if (response.ok) {
            onSuccess(); // Recarrega a lista
            handleClose();
        } else {
            alert("Erro ao excluir.");
            setShowDeleteConfirm(false);
        }
    } catch (error) {
        console.error(error);
        alert("Erro de conexão.");
    } finally {
        setLoading(false);
    }
  };

  // 💾 SALVAR (CRIAR OU EDITAR)
  const handleSubmit = async () => {
    if (!formData.titulo || !formData.data_marco) {
      alert("Por favor, preencha o título e a data.");
      return;
    }

    setLoading(true);
    
    // 🔐 PEGA O TOKEN PARA UPLOAD E SALVAMENTO
    const token = localStorage.getItem('token');

    try {
      let finalImageUrl = formData.imagem_url;

      // --- PASSO 1: UPLOAD DA FOTO (SE HOUVER NOVA) ---
      if (selectedFile) {
        const uploadData = new FormData();
        uploadData.append('file', selectedFile);

        const uploadRes = await fetch(`${API_BASE_URL}/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}` // <--- Token no Upload
          },
          body: uploadData
        });

        if (uploadRes.ok) {
          const uploadJson = await uploadRes.json();
          finalImageUrl = uploadJson.url;
        } else {
          throw new Error("Falha no upload da imagem");
        }
      }

      // --- PASSO 2: SALVAR DADOS DO MARCO ---
      const payload = {
        empreendimento_id: empreendimentoId,
        contrato_id: contratoId,
        titulo: formData.titulo,
        descricao: formData.descricao,
        data_marco: formData.data_marco,
        status: formData.status,
        imagem_url: finalImageUrl
      };

      let url = `${API_BASE_URL}/marcos`;
      let method = 'POST';

      if (marcoParaEditar && marcoParaEditar.id) {
        url = `${API_BASE_URL}/marcos/${marcoParaEditar.id}`;
        method = 'PUT';
      }

      const response = await fetch(url, {
        method: method,
        headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}` // <--- Token na Edição/Criação
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSuccess();
        handleClose();
      } else {
        const errData = await response.json();
        alert("Erro ao salvar: " + (errData.error || "Erro desconhecido"));
      }

    } catch (error) {
      console.error("Erro:", error);
      alert("Erro de conexão ao salvar.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    limparFormulario();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[95vh] relative">

        {/* ========================================================== */}
        {/* ⚠️ CARD DE CONFIRMAÇÃO DE EXCLUSÃO (OVERLAY) */}
        {/* ========================================================== */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 z-50 bg-white/95 backdrop-blur-sm flex items-center justify-center p-6 animate-fadeIn">
            <div className="text-center max-w-xs">
              <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-red-100">
                <span className="text-4xl">🗑️</span>
              </div>
              
              <h3 className="text-xl font-bold text-gray-800 mb-2">Excluir este marco?</h3>
              <p className="text-sm text-gray-500 mb-8 leading-relaxed">
                Você tem certeza que deseja remover <strong>"{formData.titulo}"</strong>? <br/>
                Essa ação não pode ser desfeita.
              </p>

              <div className="flex flex-col gap-3">
                <button
                  onClick={confirmarExclusao}
                  disabled={loading}
                  className="w-full bg-red-500 text-white font-bold py-3 rounded-xl hover:bg-red-600 hover:shadow-lg hover:shadow-red-200 transition-all active:scale-95"
                >
                  {loading ? 'Excluindo...' : 'Sim, excluir marco'}
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={loading}
                  className="w-full bg-white text-gray-500 font-bold py-3 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ========================================================== */}

        {/* Header */}
        <div className="bg-slate-50 px-8 py-5 border-b border-gray-100 flex justify-between items-center shrink-0">
          <h2 className="text-lg font-bold text-gray-800">
            {marcoParaEditar ? '✏️ Editar Marco' : '✨ Novo Marco Histórico'}
          </h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-red-500 transition-colors text-2xl leading-none">×</button>
        </div>

        {/* Body */}
        <div className="p-8 overflow-y-auto custom-scrollbar space-y-6">

          {/* Título */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 ml-1">Título do Marco</label>
            <input
              type="text"
              name="titulo"
              value={formData.titulo}
              onChange={handleChange}
              placeholder="Ex: Início das Obras"
              className="w-full p-3 bg-white border-2 border-gray-100 rounded-xl text-gray-700 font-bold focus:border-sabesp-cyan focus:ring-2 focus:ring-sabesp-cyan/20 outline-none transition-all"
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 ml-1">Descrição</label>
            <textarea
              name="descricao"
              value={formData.descricao}
              onChange={handleChange}
              rows="3"
              placeholder="Detalhes sobre esta etapa..."
              className="w-full p-3 bg-white border-2 border-gray-100 rounded-xl text-gray-700 text-sm focus:border-sabesp-cyan focus:ring-2 focus:ring-sabesp-cyan/20 outline-none transition-all resize-none"
            ></textarea>
          </div>

          {/* Data e Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 ml-1">Data (Mês/Ano)</label>
              <input
                type="text"
                name="data_marco"
                value={formData.data_marco}
                onChange={handleChange}
                placeholder="Ex: Jan 2024"
                className="w-full p-3 bg-white border-2 border-gray-100 rounded-xl text-gray-700 font-medium focus:border-sabesp-cyan focus:ring-2 focus:ring-sabesp-cyan/20 outline-none"
              />
            </div>

            {/* Status Colorido */}
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 ml-1">Status</label>
              <div className="relative">
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className={`w-full p-3 border-2 rounded-xl font-bold outline-none appearance-none cursor-pointer transition-all
                    ${formData.status === 'Concluído' 
                      ? 'bg-emerald-50 border-emerald-400 text-emerald-700 focus:ring-emerald-200' 
                      : formData.status === 'Em andamento'
                        ? 'bg-blue-50 border-blue-400 text-blue-700 focus:ring-blue-200'
                        : 'bg-white border-gray-200 text-gray-600 focus:border-gray-400'
                    } focus:ring-2`}
                >
                  <option value="A iniciar"> A iniciar</option>
                  <option value="Em andamento"> Em andamento</option>
                  <option value="Concluído"> Concluído</option>
                </select>
                <div className={`absolute right-3 top-3.5 pointer-events-none text-xs font-bold
                  ${formData.status === 'Concluído' ? 'text-emerald-600' : 
                    formData.status === 'Em andamento' ? 'text-blue-600' : 'text-gray-400'}`}>
                  ▼
                </div>
              </div>
            </div>
          </div>

          {/* Foto (Visual Novo) */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 ml-1">
              Foto do Marco (Opcional)
            </label>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              accept="image/*"
            />

            {preview ? (
              <div className="relative w-full h-48 rounded-xl overflow-hidden border-2 border-sabesp-cyan group shadow-md">
                <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all flex flex-col items-center justify-center gap-3 backdrop-blur-sm">
                  <span className="text-white text-xs font-bold uppercase tracking-widest mb-1">Editar Imagem</span>
                  <div className="flex gap-2">
                    <button type="button" onClick={handleTriggerUpload} className="bg-white text-gray-800 px-4 py-2 rounded-full text-xs font-bold hover:scale-105 transition-transform flex items-center gap-2 shadow-lg">🔄 Trocar</button>
                    <button type="button" onClick={() => { setPreview(null); setSelectedFile(null); setFormData(prev => ({ ...prev, imagem_url: '' })); }} className="bg-red-500 text-white px-4 py-2 rounded-full text-xs font-bold hover:bg-red-600 hover:scale-105 transition-all shadow-lg">🗑️ Remover</button>
                  </div>
                </div>
              </div>
            ) : (
              <div onClick={handleTriggerUpload} className="w-full h-32 rounded-xl border-2 border-dashed border-gray-300 hover:border-sabesp-cyan hover:bg-cyan-50/30 transition-all cursor-pointer flex flex-col items-center justify-center gap-2 group">
                <div className="p-3 bg-gray-100 rounded-full group-hover:bg-white group-hover:shadow-md transition-all">
                  <span className="text-2xl opacity-50 group-hover:opacity-100 group-hover:scale-110 transition-transform block">📷</span>
                </div>
                <span className="text-xs font-bold text-gray-400 group-hover:text-sabesp-cyan uppercase tracking-wide">Clique para adicionar foto</span>
              </div>
            )}
          </div>

        </div>

        {/* Footer */}
        <div className="flex justify-between items-center bg-gray-50 px-8 py-5 border-t border-gray-100 gap-3 shrink-0">
          
          {/* Botão EXCLUIR (Só aparece se estiver editando) */}
          {marcoParaEditar?.id ? (
            <button
              type="button"
              onClick={solicitarExclusao} // Chama o Card de Confirmação
              className="text-red-500 hover:text-red-700 font-bold text-sm flex items-center gap-1 hover:bg-red-50 px-3 py-2 rounded-lg transition-colors"
            >
              🗑️ Excluir
            </button>
          ) : (
            <div></div> // Espaçador
          )}

          <div className="flex gap-3">
            <button onClick={handleClose} className="px-6 py-2.5 rounded-xl text-gray-500 font-bold hover:bg-gray-200 transition-colors text-sm">Cancelar</button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-8 py-2.5 rounded-xl bg-sabesp-cyan text-white font-bold hover:bg-cyan-600 transition-all shadow-lg hover:shadow-xl text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Salvando...' : (marcoParaEditar ? '💾 Salvar' : '➕ Adicionar')}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

export default AdicionarMarcoModal;